import { Add as AddIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { AllSystemCSSProperties } from '@mui/system/styleFunctionSx/styleFunctionSx';
import * as React from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { Dialog, ICallableDialogRef, IDialogContainerProps } from '@/wrappers/Dialog';
import { Drawer } from '@/wrappers/Drawer';
import { IconButton } from '@/wrappers/IconButton';
import FormSettingsContext from '../FormSettingsContext';
import { FieldsGenerator } from './FieldsGenerator';
import { ISupportedFieldType } from './FieldTypes';
import FormWrapper from './FormWrapper';
import { getValuesFromProps, ISupportedValueType, IValueType } from './utils';

export type IOutputValueType = ISupportedValueType;

type IBaseProps = {
    innerRef?: React.Ref<ICallableRef>;
    /** Only for tests */
    name: string;
    displayAsModal?: boolean;
    displayAsSidebar?: boolean;
    error?: boolean;
    icon?: React.ReactElement;
    isEdit?: boolean;
    justIcon?: boolean;
    readOnly?: boolean;
    noFullWidth?: boolean;
    fields: (ISupportedFieldType | false)[];
    actions?: ISupportedFieldType[];
    maxWidth?: IDialogContainerProps['maxWidth'];
    title?: React.ReactNode;
    subTitle?: React.ReactNode;
    openButtonRender?: (onClick: (event: React.MouseEvent) => void) => React.ReactElement;
    openButtonValue?: string;
    isLoaded?: boolean;
    justifyContent?: AllSystemCSSProperties['justifyContent'];
    onOpen?: () => void;
    onSubmit?: (values: IOutputValueType, buttonName: string) => void;
};
type IModalProps = Omit<IBaseProps, 'title'> & {
    title: React.ReactNode;
} & ({
        displayAsModal: true;
    } & {
        displayAsSidebar: true;
    });
type IPageFormProps = IBaseProps & {
    displayAsModal: false;
    displayAsSidebar: false;
};
export type IFormGeneratorProps = IBaseProps | IModalProps | IPageFormProps;

type IOpenIButtonProps = {
    name: string;
    render?: (onClick: (event: React.MouseEvent) => void) => React.ReactNode;
    openButtonValue?: string;
    justIcon: boolean;
    onClick: () => void;
    isEdit: boolean;
    icon?: React.ReactElement;
};

const StyledOpenButton = styled(IconButton, {
    shouldForwardProp: (propName) => 'isEdit' !== propName && 'hasError' !== propName
})<{ isEdit?: boolean; hasError?: boolean }>(
    ({ theme, isEdit, hasError }) => `
        color: ${hasError ? theme.palette.error.main : isEdit ? theme.palette.info.main : theme.palette.primary.main};
    `
);

const StyleButton = styled(IconButton)(
    ({ theme }) => `
        color: ${theme.palette.text.primary};
    `
);

export function OpenButton(props: IOpenIButtonProps) {
    const render =
        props.render ||
        (props.justIcon
            ? (onClick: (event: React.MouseEvent) => void) =>
                  props.icon ? (
                      <StyleButton
                          name="openButton"
                          onClick={onClick}
                          title={props.openButtonValue}
                          data-testid={`openDialog-${props.name}`}
                      >
                          {props.icon}
                      </StyleButton>
                  ) : (
                      <StyledOpenButton
                          name="openButton"
                          onClick={onClick}
                          title={props.openButtonValue}
                          isEdit={props.isEdit}
                          data-testid={`openDialog-${props.name}`}
                      >
                          {props.isEdit ? <EditIcon /> : <AddIcon />}
                      </StyledOpenButton>
                  )
            : undefined);

    return <Dialog.OpenButton onClick={props.onClick} value={props.openButtonValue} render={render} />;
}

export type ICallableRef = {
    setFieldValue: (name: string, value: IValueType) => void;
    getFieldValue: (name: string) => IValueType;
    setOpen: (open: boolean) => void;
};

function clearEmptyArrayFormValues(data: FieldValues) {
    Object.keys(data).map((key) => {
        const attribute = data[`${key}`];

        if (Array.isArray(attribute)) {
            data[`${key}`] = attribute.filter((arrayItem: FieldValues) => Object.keys(arrayItem).length !== 0);
        }

        return key;
    });

    return data;
}

const mapActions = (actions: ISupportedFieldType[], handleCancel: () => void): ISupportedFieldType[] =>
    actions.map((action) =>
        action.type === 'button' && action.props.type === 'cancel'
            ? {
                  ...action,
                  props: { ...action.props, type: 'button', onClick: handleCancel }
              }
            : action
    );

export const FormGenerator = React.forwardRef<HTMLFormElement, IFormGeneratorProps>(function FormGeneratorForward(
    {
        innerRef,
        actions = [],
        displayAsModal,
        displayAsSidebar,
        fields = [],
        icon,
        isEdit,
        isLoaded,
        justifyContent,
        justIcon,
        maxWidth,
        name,
        noFullWidth,
        openButtonRender,
        openButtonValue,
        readOnly,
        subTitle,
        title,
        onOpen,
        onSubmit
    },
    ref
) {
    const { formState, reset, trigger, getValues, setValue, ...formMethods } = useForm({
        mode: 'all',
        criteriaMode: 'firstError',
        shouldFocusError: true
    });
    const drawerRef = React.useRef<ICallableDialogRef | null>(null);
    const modalRef = React.useRef<ICallableDialogRef | null>(null);
    const contextSettings = React.useContext(FormSettingsContext);
    const enableDomRequired = contextSettings.enableDOMAttributeRequired;

    const filteredField = fields.filter((field) => field !== false) as ISupportedFieldType[];

    const fullWidthForm = noFullWidth !== true;
    const handleClose = React.useCallback(() => {
        reset();
        if (displayAsSidebar && drawerRef.current) {
            drawerRef.current?.setOpen(false);
        }

        if (displayAsModal && modalRef.current) {
            modalRef.current?.setOpen(false);
        }
    }, [reset, displayAsModal, displayAsSidebar]);
    const handleOpen = React.useCallback(() => {
        reset();

        if (displayAsSidebar && drawerRef.current) {
            drawerRef.current?.setOpen(true);
        }

        if (displayAsModal && modalRef.current) {
            modalRef.current?.setOpen(true);
        }

        if (onOpen) {
            onOpen();
        }

        const data = getValuesFromProps(filteredField);

        Object.keys(data).forEach((fieldName) => {
            setValue(fieldName, data[fieldName]);
        });
    }, [filteredField, reset, setValue, displayAsSidebar, displayAsModal, onOpen]);

    const handleOnSubmit = async (event: React.FormEvent<HTMLFormElement>, buttonName: string) => {
        // eslint-disable-next-line no-console
        console.log(readOnly, event);

        if (readOnly) {
            return;
        }

        const isValid = await trigger();

        if (Object.keys(formState.errors).length || !isValid) {
            return;
        }

        const filledValues = clearEmptyArrayFormValues(getValues());

        if (onSubmit) {
            onSubmit(filledValues, buttonName);
        }
    };

    React.useEffect(() => {
        const data = getValues();

        Object.keys(data).forEach((fieldName) => {
            setValue(fieldName, data[fieldName]);
        });
    }, [fields, getValues, setValue]);

    React.useEffect(() => {
        if (isLoaded) {
            const data = getValuesFromProps(filteredField);

            Object.keys(data).forEach((fieldName) => {
                setValue(fieldName, data[fieldName]);
            });
        }
    }, [filteredField, isLoaded, setValue]);

    React.useImperativeHandle(innerRef, () => ({
        setOpen: (newState = true) => {
            if (displayAsSidebar && drawerRef.current) {
                drawerRef.current?.setOpen(newState);
            }

            if (displayAsModal && modalRef.current) {
                modalRef.current?.setOpen(newState);
            }
        },
        setFieldValue: (nameOfField: string, value: IValueType) => {
            setValue(nameOfField, value);
        },
        getFieldValue: (nameOfField: string) => {
            return getValues()[nameOfField];
        }
    }));

    if (displayAsModal) {
        return (
            <>
                <OpenButton
                    name={`${name}_openButton`}
                    openButtonValue={openButtonValue}
                    isEdit={!!isEdit}
                    justIcon={!!justIcon}
                    icon={icon}
                    onClick={handleOpen}
                    render={openButtonRender}
                />
                <Dialog.Container
                    innerRef={modalRef}
                    fullWidth={fullWidthForm}
                    maxWidth={maxWidth}
                    onClose={handleClose}
                    data-testid={`dialog-${name}`}
                >
                    <Dialog.Title
                        onClose={subTitle ? undefined : handleClose}
                        subTitle={subTitle}
                        data-testid="dialog-title"
                    >
                        {title}
                    </Dialog.Title>
                    <FormWrapper
                        isLoaded={isLoaded}
                        methods={{
                            ...formMethods,
                            formState,
                            getValues,
                            reset,
                            trigger,
                            setValue
                        }}
                        name={name}
                        ref={ref}
                        readOnly={readOnly}
                        onSubmit={handleOnSubmit}
                    >
                        <Dialog.Content>
                            <FieldsGenerator
                                justifyContent={justifyContent}
                                fields={filteredField}
                                fullWidth={fullWidthForm}
                            />
                        </Dialog.Content>
                        <Dialog.Actions>
                            <FieldsGenerator
                                justifyContent={justifyContent}
                                actions
                                fields={mapActions(actions ?? [], handleClose)}
                                fullWidth={fullWidthForm}
                            />
                        </Dialog.Actions>
                    </FormWrapper>
                </Dialog.Container>
            </>
        );
    }

    if (displayAsSidebar) {
        return (
            <>
                <OpenButton
                    icon={icon}
                    name={`${name}_openButton`}
                    openButtonValue={openButtonValue}
                    isEdit={!!isEdit}
                    justIcon={!!justIcon}
                    onClick={handleOpen}
                    render={openButtonRender}
                />
                <Drawer.Container
                    innerRef={drawerRef}
                    name={name}
                    size={!maxWidth ? undefined : maxWidth}
                    data-testid={`drawer-${name}`}
                    onClose={handleClose}
                >
                    {title && (
                        <Drawer.Title sx={{ paddingInline: 1 }} data-testid="title">
                            {title}
                        </Drawer.Title>
                    )}
                    {subTitle && <Drawer.Subtitle sx={{ paddingInline: 1 }}>{subTitle}</Drawer.Subtitle>}
                    <FormWrapper
                        isLoaded={isLoaded}
                        name={name}
                        ref={ref}
                        methods={{
                            ...formMethods,
                            formState,
                            getValues,
                            reset,
                            trigger,
                            setValue
                        }}
                        readOnly={readOnly}
                        onSubmit={handleOnSubmit}
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Drawer.Content>
                            <FieldsGenerator
                                justifyContent={justifyContent}
                                fields={filteredField}
                                fullWidth={fullWidthForm}
                            />
                        </Drawer.Content>
                        <Drawer.Actions sx={{ justifyContent: 'space-between' }}>
                            {actions && actions.length > 1 ? (
                                <FieldsGenerator
                                    actions
                                    fields={mapActions(actions.slice(0, 1), handleClose)}
                                    fullWidth={fullWidthForm}
                                />
                            ) : (
                                <Box />
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <FieldsGenerator
                                    justifyContent={justifyContent}
                                    actions
                                    fields={mapActions(
                                        actions && actions.length === 1 ? actions : (actions ?? []).slice(1),
                                        handleClose
                                    )}
                                    fullWidth={fullWidthForm}
                                />
                            </Box>
                        </Drawer.Actions>
                    </FormWrapper>
                </Drawer.Container>
            </>
        );
    }

    return (
        <FormWrapper
            methods={{
                ...formMethods,
                formState,
                getValues,
                reset,
                trigger,
                setValue
            }}
            isLoaded={isLoaded}
            name={name}
            ref={ref}
            readOnly={readOnly}
            onSubmit={handleOnSubmit}
        >
            <FieldsGenerator
                readOnly={readOnly}
                enableDOMRequired={enableDomRequired}
                justifyContent={justifyContent}
                fields={filteredField}
                fullWidth={fullWidthForm}
            />
            {!readOnly && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        {(actions ?? []).length > 1 ? (
                            <FieldsGenerator
                                actions
                                fields={mapActions((actions ?? []).slice(0, 1), handleClose)}
                                fullWidth={fullWidthForm}
                            />
                        ) : (
                            <></>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <FieldsGenerator
                            justifyContent={justifyContent}
                            actions
                            fields={mapActions(
                                actions && actions.length === 1 ? actions : (actions ?? []).slice(1),
                                handleClose
                            )}
                            fullWidth={fullWidthForm}
                        />
                    </Box>
                </Box>
                // <FieldsGenerator
                //     {...formMethods}
                //     enableDOMRequired={enableDomRequired}
                //     justifyContent={justifyContent}
                //     getValues={getValues}
                //     initialValues={initialValues}
                //     setValue={setValue}
                //     actions
                //     fields={actions}
                //     fullWidth={false}
                // />
            )}
        </FormWrapper>
    );
});
